<template>
  <div data-cy="inventory-part-search" class="relative">
    <input
      v-if="!selectedPart"
      ref="inputRef"
      v-model="query"
      type="text"
      placeholder="Search parts..."
      class="w-full text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 p-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
      @input="onInput"
      @focus="showResults = results.length > 0"
      @blur="onBlur"
    />
    <div
      v-else
      class="flex items-center justify-between w-full text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 p-2 cursor-pointer hover:border-gray-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
      @click="clearSelection"
    >
      <span class="truncate">{{ selectedPart.name }}</span>
      <svg
        class="w-3 h-3 shrink-0 text-gray-400 ml-1"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    </div>
    <div
      v-if="showResults && !selectedPart"
      class="absolute left-0 right-0 z-50 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg dark:bg-gray-700 dark:border-gray-600 max-h-48 overflow-y-auto"
    >
      <div
        v-if="loading"
        class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400"
      >
        Searching...
      </div>
      <template v-else-if="results.length > 0">
        <div
          v-for="part in results"
          :key="part.id"
          data-cy="inventory-part-option"
          class="px-3 py-2 text-sm text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
          @mousedown.prevent="selectPart(part)"
        >
          {{ part.name }}
        </div>
      </template>
      <div
        v-else-if="query.length > 0"
        class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400"
      >
        No results
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { nextTick, ref, watch } from "vue";
import { useStore } from "vuex";

import type { InventoryProduct } from "@/typings/inventory";

interface PartSelection {
  id: number;
  name: string;
}

const props = defineProps<{
  modelValue: PartSelection | null;
}>();

const emit = defineEmits<{
  select: [part: PartSelection];
  clear: [];
}>();

const store = useStore();
const query = ref("");
const results = ref<InventoryProduct[]>([]);
const showResults = ref(false);
const loading = ref(false);
const inputRef = ref<HTMLInputElement | null>(null);
const selectedPart = ref<PartSelection | null>(null);

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

const onInput = () => {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(async () => {
    if (query.value.length === 0) {
      results.value = [];
      showResults.value = false;
      return;
    }
    loading.value = true;
    showResults.value = true;
    results.value = await store.dispatch("fetchInventoryParts", {
      search: query.value
    });
    loading.value = false;
  }, 300);
};

const selectPart = (part: InventoryProduct) => {
  selectedPart.value = { id: part.id, name: part.name };
  query.value = "";
  results.value = [];
  showResults.value = false;
  emit("select", { id: part.id, name: part.name });
};

const clearSelection = () => {
  selectedPart.value = null;
  query.value = "";
  emit("clear");
  nextTick(() => inputRef.value?.focus());
};

const onBlur = () => {
  setTimeout(() => {
    showResults.value = false;
  }, 200);
};

watch(
  () => props.modelValue,
  (val) => {
    selectedPart.value = val;
  },
  { immediate: true }
);
</script>
